/**
 * useCornerResize — bottom-right corner drag-to-resize for a floating panel (popover).
 *
 * Unlike useColumnResize (width-only, backed by a persisted column list), this hook owns
 * no "default" size of its own. Before the first drag, `size` is `null` and the caller
 * applies no width/height override at all, so the panel keeps its normal auto/content-driven
 * sizing — and whatever max-height/max-width viewport-fit ceiling it already computes —
 * exactly as before this hook existed. The first pointerdown reads the panel's *actual
 * current* rendered box via `targetRef` (not a hardcoded default) as the drag baseline, so
 * grabbing the handle never causes a jump.
 *
 * `minWidth`/`minHeight`/`maxWidth`/`maxHeight` are optional soft clamps applied to the
 * dragged size itself; a popover's own CSS `max-width`/`max-height` (static or the
 * `var(--radix-popper-available-height)` viewport-fit cap) still wins as the final visual
 * ceiling regardless of what's passed here, so it's safe to leave them at the defaults and
 * let the existing CSS do the collision-safety work.
 */
import { useCallback, useRef, useState, type RefObject } from 'react';

export interface CornerResizeSize {
  width: number;
  height: number;
}

interface UseCornerResizeOptions {
  minWidth?: number;
  minHeight?: number;
  maxWidth?: number;
  maxHeight?: number;
}

const DEFAULT_MIN_WIDTH = 280;
const DEFAULT_MIN_HEIGHT = 160;

export function useCornerResize(
  targetRef: RefObject<HTMLElement | null>,
  { minWidth = DEFAULT_MIN_WIDTH, minHeight = DEFAULT_MIN_HEIGHT, maxWidth = Infinity, maxHeight = Infinity }: UseCornerResizeOptions = {},
) {
  const [size, setSize] = useState<CornerResizeSize | null>(null);
  const sizeRef = useRef(size);
  sizeRef.current = size;
  const draggingRef = useRef(false);
  const rafRef = useRef<number | null>(null);

  const onResizeStart = useCallback((e: React.PointerEvent) => {
    // Popovers render `onClick={(e) => e.stopPropagation()}` at the content root to keep
    // clicks from bubbling to an outer clickable row — stopPropagation here too so a drag
    // never gets misread as such a row click, and preventDefault so text selection doesn't
    // start while dragging.
    e.preventDefault();
    e.stopPropagation();
    const rect = targetRef.current?.getBoundingClientRect();
    if (!rect) return;

    draggingRef.current = true;
    const startX = e.clientX;
    const startY = e.clientY;
    const startWidth = sizeRef.current?.width ?? rect.width;
    const startHeight = sizeRef.current?.height ?? rect.height;

    const onMove = (ev: PointerEvent) => {
      if (!draggingRef.current) return;
      const nextWidth = Math.min(maxWidth, Math.max(minWidth, startWidth + (ev.clientX - startX)));
      const nextHeight = Math.min(maxHeight, Math.max(minHeight, startHeight + (ev.clientY - startY)));
      // Throttle React state updates to once per animation frame for smoothness.
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        setSize({ width: nextWidth, height: nextHeight });
      });
    };
    const onUp = () => {
      draggingRef.current = false;
      if (rafRef.current !== null) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.body.style.cursor = 'nwse-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
  }, [targetRef, minWidth, minHeight, maxWidth, maxHeight]);

  return { size, onResizeStart };
}
